/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file mailThreadChat.js
 * @description
 * Pure helpers for the PRIVATE DISCUSSION under a shared-mailbox mail thread (Happy Mailer): the
 * compact panel in `MailThreadScreen` and its full-height sheet. Free of React / network code so
 * they are unit-testable. Contract: `GET|POST /api/mail/collab/threads/chat*` (messages are the
 * generic team-chat messages, newest first; thread state carries an additive `chat` summary).
 *
 * Everything is shape-guarded (Hermes): `sender` may be null, names may be objects, ids ObjectIds.
 */

import {idOf, personName, textOf} from './teamChat/chatShapes';

/** Push / notification event keys introduced by the thread discussion. */
export const THREAD_CHAT_EVENT_KEYS = ['mail.thread_comment', 'mail.thread_mention'];

/** Backend cap of @mentions per comment (`MAIL_CHAT_TOO_MANY_MENTIONS`). */
export const MAX_MENTIONS = 20;

/** Max members offered by the @mention / assign pickers at once. */
export const MAX_PICKER_ROWS = 6;

const lower = (value) => String(value || '').trim().toLowerCase();

/**
 * Whether a push payload belongs to the thread discussion (comment or @mention).
 *
 * @param {Object} data Notification `data` / `metadata` payload.
 * @returns {boolean} True for one of {@link THREAD_CHAT_EVENT_KEYS}.
 */
export const isThreadChatEvent = (data) => THREAD_CHAT_EVENT_KEYS.includes(lower(data?.eventKey || data?.eventkey));

/**
 * Compact summary used by the row badge / preview card.
 *
 * @param {Object|null} state Thread state (`state.chat` = `{channelId,count,unreadCount,lastAt,lastBy}`).
 * @returns {{channelId: string, count: number, unread: number, lastAt: string, lastBy: string}} Summary (zeros when none).
 */
export const chatSummary = (state) => {
    const chat = state && typeof state === 'object' ? state.chat : null;
    const count = Number(chat?.count);
    const unread = Number(chat?.unreadCount);

    return {
        channelId: idOf(chat?.channelId),
        count: Number.isFinite(count) && count > 0 ? Math.floor(count) : 0,
        unread: Number.isFinite(unread) && unread > 0 ? Math.floor(unread) : 0,
        lastAt: chat?.lastAt ? String(chat.lastAt) : '',
        lastBy: textOf(chat?.lastBy),
    };
};

/**
 * Applies a realtime `mail:thread_chat` event to a (possibly missing) thread state: bumps the
 * comment count and, for somebody else's comment, the unread counter. Never mutates the input.
 * The caller follows up with an authoritative state refresh, so this only has to feel instant.
 *
 * @param {Object|null} state Current thread state.
 * @param {{authorId?: string}} payload Socket payload.
 * @param {string} meId Signed-in user id.
 * @param {number} [now] Reference time (ms).
 * @returns {Object} New state (a stub carrying only `chat` when there was none).
 */
export const applyChatEventToState = (state, payload, meId, now = Date.now()) => {
    const base = state && typeof state === 'object' ? state : {};
    const previous = chatSummary(base);
    const mine = Boolean(meId) && idOf(payload?.authorId) === String(meId);

    return {
        ...base,
        chat: {
            channelId: previous.channelId,
            count: previous.count + 1,
            unreadCount: previous.unread + (mine ? 0 : 1),
            lastAt: new Date(now).toISOString(),
            lastBy: base.chat?.lastBy || '',
        },
    };
};

/**
 * Same state with the unread counter cleared (after the discussion was opened and marked read).
 *
 * @param {Object|null} state Thread state.
 * @returns {Object|null} New state, or the input when there is no chat / nothing unread.
 */
export const clearChatUnread = (state) => {
    if (!state || !state.chat || !Number(state.chat.unreadCount)) {
        return state;
    }

    return {...state, chat: {...state.chat, unreadCount: 0}};
};

/**
 * Stable id of a message (real `_id`, falling back to the client id of an optimistic row).
 *
 * @param {Object} message Chat message.
 * @returns {string} Id ('' when none).
 */
export const messageKey = (message) => idOf(message?._id || message?.id) || String(message?.clientMessageId || '');

/**
 * New client-generated id, echoed back by the server so the realtime copy can be de-duplicated.
 *
 * @param {number} [now] Timestamp.
 * @param {number} [random] Random fraction.
 * @returns {string} Id.
 */
export const newClientMessageId = (now = Date.now(), random = Math.random()) => `mailchat:${now.toString(36)}:${Math.floor(random * 1e9).toString(36)}`;

/**
 * Builds the optimistic (pending) row shown before the server answers.
 *
 * @param {{text: string, me: {_id?: string, id?: string, name?: *}, clientMessageId: string, now?: number}} params Params.
 * @returns {Object} Pending message.
 */
export const buildOptimisticMessage = ({text, me, clientMessageId, now = Date.now()}) => ({
    _id: `pending:${clientMessageId}`,
    clientMessageId,
    kind: 'text',
    text: String(text || ''),
    sender: {_id: idOf(me), name: personName(me, '')},
    createdAt: new Date(now).toISOString(),
    pending: true,
});

const timeOf = (message) => {
    const time = new Date(message?.createdAt).getTime();

    return Number.isFinite(time) ? time : 0;
};

/**
 * Merges an incoming page into the list (newest first), de-duplicating by real id AND by
 * `clientMessageId` - so the server copy / the realtime echo REPLACES the optimistic row instead
 * of appearing twice. Server rows win over pending ones; order is by `createdAt` descending
 * (stable for equal times).
 *
 * @param {Object[]} current Current list.
 * @param {Object[]} incoming Incoming messages.
 * @returns {Object[]} New list.
 */
export const mergeMessages = (current, incoming) => {
    const byId = new Map();
    const byClient = new Map();
    const out = [];

    const put = (message, isServer) => {
        if (!message || typeof message !== 'object') {
            return;
        }
        const id = messageKey(message);
        const client = String(message.clientMessageId || '');
        const existingIndex = byId.has(id) ? byId.get(id) : (client && byClient.has(client) ? byClient.get(client) : -1);

        if (existingIndex >= 0) {
            if (isServer || out[existingIndex].pending) {
                out[existingIndex] = isServer ? {...message, pending: false} : message;
                byId.set(id, existingIndex);
            }

            return;
        }
        out.push(isServer ? {...message, pending: false} : message);
        byId.set(id, out.length - 1);
        if (client) {
            byClient.set(client, out.length - 1);
        }
    };

    (Array.isArray(incoming) ? incoming : []).forEach((message) => put(message, !message?.pending));
    (Array.isArray(current) ? current : []).forEach((message) => put(message, false));

    return out
        .map((message, index) => ({message, index}))
        .sort((a, b) => (timeOf(b.message) - timeOf(a.message)) || (a.index - b.index))
        .map((entry) => entry.message);
};

/**
 * Removes one row (rollback of a failed optimistic send).
 *
 * @param {Object[]} list Messages.
 * @param {string} clientMessageId Client id of the row.
 * @returns {Object[]} New list.
 */
export const removeByClientId = (list, clientMessageId) => (Array.isArray(list) ? list : [])
    .filter((message) => message?.clientMessageId !== clientMessageId);

/**
 * Index (in a newest-first list) of the OLDEST unread message - the unread divider is drawn right
 * above that row. Only the first `unreadCount` rows from somebody else count as unread.
 *
 * @param {Object[]} messages Newest-first messages.
 * @param {number} unreadCount Unread count of the caller.
 * @param {string} meId Signed-in user id.
 * @returns {number} Index, or -1 when nothing is unread.
 */
export const unreadDividerIndex = (messages, unreadCount, meId) => {
    const list = Array.isArray(messages) ? messages : [];
    let remaining = Math.max(0, Math.floor(Number(unreadCount) || 0));
    let found = -1;

    for (let index = 0; index < list.length && remaining > 0; index += 1) {
        if (list[index]?.pending || (meId && idOf(list[index]?.sender) === String(meId)) || list[index]?.kind === 'system') {
            continue;
        }
        found = index;
        remaining -= 1;
    }

    return found;
};

/** Display name of a member row (`{_id,name,...}`) - never an object. */
const memberName = (member) => personName(member, '');
const memberId = (member) => idOf(member?._id || member?.id || member?.userId || member?.user);

/**
 * The "@query" fragment being typed at the END of the text.
 *
 * @param {string} text Composer text.
 * @returns {{query: string, start: number}|null} Active mention fragment, or null.
 */
export const activeMentionQuery = (text) => {
    const match = /(^|\s)@([^\s@]{0,40})$/.exec(String(text || ''));

    if (!match) {
        return null;
    }

    return {query: match[2], start: match.index + match[1].length};
};

/**
 * Members offered by the @mention / assign picker: MEMBERS ONLY, never the caller, filtered by
 * name/email substring (prefix matches first), capped.
 *
 * @param {Object[]} members Mailbox members.
 * @param {string} query Typed fragment.
 * @param {string} meId Signed-in user id.
 * @param {number} [limit] Max rows.
 * @returns {Object[]} Members.
 */
export const mentionCandidates = (members, query, meId, limit = MAX_PICKER_ROWS) => {
    const needle = lower(query);
    const list = (Array.isArray(members) ? members : [])
        .filter((member) => memberId(member) && memberId(member) !== String(meId || '') && memberName(member));

    if (!needle) {
        return list.slice(0, limit);
    }

    const scored = list
        .map((member) => {
            const hay = `${lower(memberName(member))} ${lower(member?.email)}`;
            const at = hay.indexOf(needle);

            return {member, at, prefix: lower(memberName(member)).startsWith(needle)};
        })
        .filter((entry) => entry.at >= 0);

    scored.sort((a, b) => Number(b.prefix) - Number(a.prefix));

    return scored.slice(0, limit).map((entry) => entry.member);
};

/**
 * Replaces the active "@fragment" with "@Name " (the picker's insertion).
 *
 * @param {string} text Composer text.
 * @param {{start: number}} active Result of {@link activeMentionQuery}.
 * @param {Object} member Picked member.
 * @returns {string} New text.
 */
export const insertMention = (text, active, member) => {
    const source = String(text || '');
    const start = Math.max(0, Math.min(Number(active?.start) || 0, source.length));

    return `${source.slice(0, start)}@${memberName(member)} `;
};

/**
 * Member ids mentioned in the text ("@Full Name", case-insensitive, longest names first so
 * "@Ann Lee" is not read as "@Ann"). Members only; the caller is skipped; capped to the backend limit.
 *
 * @param {string} text Final comment text.
 * @param {Object[]} members Mailbox members.
 * @param {string} meId Signed-in user id.
 * @returns {string[]} Unique member ids.
 */
export const extractMentionIds = (text, members, meId) => {
    const haystack = lower(text);
    const seen = new Set();
    const claimed = [];
    const ids = [];
    const ordered = (Array.isArray(members) ? members : [])
        .filter((member) => memberId(member) && memberName(member))
        .sort((a, b) => memberName(b).length - memberName(a).length);

    ordered.forEach((member) => {
        const id = memberId(member);
        const needle = `@${lower(memberName(member))}`;
        let at = haystack.indexOf(needle);

        // First occurrence that is not part of a LONGER member's mention ("@Ann Lee" must not tag "Ann").
        while (at >= 0) {
            const end = at + needle.length;
            const after = haystack.charAt(end);
            const overlaps = claimed.some(([from, to]) => at < to && end > from);
            const wordContinues = Boolean(after) && /[\p{L}\p{N}_]/u.test(after);

            if (!overlaps && !wordContinues) {
                claimed.push([at, end]);
                break;
            }
            at = haystack.indexOf(needle, at + 1);
        }

        if (at < 0 || seen.has(id) || id === String(meId || '')) {
            return;
        }
        seen.add(id);
        ids.push(id);
    });

    return ids.slice(0, MAX_MENTIONS);
};

/**
 * Classifies a failed chat call by the backend `msg` code / status.
 *
 * @param {{status?: number, msg?: string, payload?: Object}} error Error from `mailApi`.
 * @returns {'takeover'|'conflict'|'not_member'|'too_many'|'text_required'|'forbidden'|'not_shared'|'other'} Kind.
 */
export const chatErrorKind = (error) => {
    const code = String(error?.msg || error?.payload?.msg || '');
    const status = Number(error?.status);

    if (code === 'MAIL_THREAD_ASSIGNED') {
        return 'takeover';
    }
    if (code === 'MAIL_THREAD_CONFLICT' || status === 409) {
        return 'conflict';
    }
    if (code === 'MAIL_CHAT_MENTION_NOT_MEMBER' || code === 'MAIL_ASSIGNEE_NOT_MEMBER') {
        return 'not_member';
    }
    if (code === 'MAIL_CHAT_TOO_MANY_MENTIONS') {
        return 'too_many';
    }
    if (code === 'MAIL_CHAT_TEXT_REQUIRED') {
        return 'text_required';
    }
    if (code === 'NOT_SHARED') {
        return 'not_shared';
    }
    if (status === 403 || status === 401) {
        return 'forbidden';
    }

    return 'other';
};

/**
 * Normalises the `GET /threads/chat` envelope.
 *
 * @param {Object} response Raw response.
 * @returns {{chatAvailable: boolean, channel: Object|null, messages: Object[], hasMore: boolean, members: Object[]}} Safe shape.
 */
export const normalizeChatResponse = (response) => {
    const available = response?.chatAvailable !== false;

    return {
        chatAvailable: available,
        channel: available && response?.channel && typeof response.channel === 'object' ? response.channel : null,
        messages: available && Array.isArray(response?.messages) ? response.messages.filter((m) => m && typeof m === 'object') : [],
        hasMore: available && Boolean(response?.hasMore),
        members: available && Array.isArray(response?.members) ? response.members : [],
    };
};

/**
 * Navigation params for a push about a thread discussion: opens the reader AND the discussion sheet.
 *
 * @param {Object} data Notification payload.
 * @returns {Object} `{openChat: true}` for comment / mention pushes, `{}` otherwise.
 */
export const chatOpenParams = (data) => (isThreadChatEvent(data) ? {openChat: true} : {});

/**
 * Turns the `GET /collab/threads/chat/target` response into `MailThreadScreen` route params (the
 * same `messageId` / `threadKey` / `subject` shape `notificationNavigation` builds for mail pushes).
 *
 * @param {{mailMessageId?: string, threadKey?: string, subject?: string}|null} response Target payload.
 * @returns {Object|null} Params, or null when the caller has no readable copy of the mail.
 */
export const mailTargetParams = (response) => {
    const messageId = idOf(response?.mailMessageId);

    if (!messageId) {
        return null;
    }
    const threadKey = textOf(response?.threadKey);
    const subject = textOf(response?.subject);

    return {messageId, ...(threadKey ? {threadKey} : {}), ...(subject ? {subject} : {})};
};

/**
 * Splits a comment into plain / mention segments so "@Name" renders highlighted. Only names of
 * known members are highlighted (same longest-first rule as {@link extractMentionIds}).
 *
 * @param {string} text Comment text.
 * @param {Object[]} members Mailbox members.
 * @returns {{text: string, mention: boolean}[]} Segments (never empty for non-empty text).
 */
export const splitByMentions = (text, members) => {
    const source = String(text || '');
    const names = (Array.isArray(members) ? members : [])
        .map(memberName)
        .filter(Boolean)
        .sort((a, b) => b.length - a.length);

    if (!source || !names.length) {
        return source ? [{text: source, mention: false}] : [];
    }

    const lowered = source.toLowerCase();
    const segments = [];
    let cursor = 0;

    while (cursor < source.length) {
        const at = lowered.indexOf('@', cursor);

        if (at < 0) {
            break;
        }
        const hit = names.find((name) => lowered.startsWith(`@${name.toLowerCase()}`, at));
        const end = hit ? at + hit.length + 1 : -1;

        if (!hit || (at > 0 && !/\s/.test(source.charAt(at - 1))) || (end < source.length && /[\p{L}\p{N}_]/u.test(source.charAt(end)))) {
            segments.push({text: source.slice(cursor, at + 1), mention: false});
            cursor = at + 1;
            continue;
        }
        if (at > cursor) {
            segments.push({text: source.slice(cursor, at), mention: false});
        }
        segments.push({text: source.slice(at, end), mention: true});
        cursor = end;
    }
    if (cursor < source.length) {
        segments.push({text: source.slice(cursor), mention: false});
    }

    // Merge neighbouring plain runs (skipped "@" characters split them).
    return segments.reduce((acc, segment) => {
        const last = acc[acc.length - 1];

        if (last && !last.mention && !segment.mention) {
            last.text += segment.text;
        } else {
            acc.push({...segment});
        }

        return acc;
    }, []);
};
